import { z } from 'zod'
import type { MediaRequirements } from './account.ts'
import { MediaRequirementsSchema } from './account.ts'
import { idSchema, msTimestampSchema } from './common.ts'
import { LLMFailureDiagnosticSchema } from './llm-provider.ts'

/**
 * How Rally normalizes BDA output into an analysis result. Bump this whenever normalization changes
 * the *meaning* of a stored field, so results produced by the old normalization become re-analyzable
 * (see isStaleNormalization). Results predating this field read as version 1.
 *
 * 1 -> original.
 * 2 -> RAL-197: MediaProbe records display dimensions, not BDA's pre-rotation encoded frame size.
 */
export const CURRENT_NORMALIZATION_VERSION = 2
export const normalizationVersionSchema = z.number().int().positive().default(1)

export const AnalysisStatusEnum = z.enum(['pending', 'analyzing', 'succeeded', 'failed'])
export type AnalysisStatus = z.infer<typeof AnalysisStatusEnum>

// Shared confirmation coordinates intentionally do not inherit the ledger's three-attempt cap.
export const AssetAnalysisCoordinatesSchema = z
  .object({
    generation: z.number().int().positive(),
    attempt: z.number().int().positive()
  })
  .strict()
export type AssetAnalysisCoordinates = z.infer<typeof AssetAnalysisCoordinatesSchema>

export const MediaProbeSchema = z
  .object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    durationSeconds: z.number().positive()
  })
  .strict()
export type MediaProbe = z.infer<typeof MediaProbeSchema>

export type MediaProbeCheckResults = {
  aspectRatioPass: boolean
  durationPass: boolean
}

export const getMediaProbeCheckResults = (
  probe: MediaProbe,
  requirements: MediaRequirements
): MediaProbeCheckResults => ({
  aspectRatioPass:
    Math.abs(
      probe.width / probe.height - requirements.requiredAspectRatio.width / requirements.requiredAspectRatio.height
    ) < 0.02,
  durationPass: probe.durationSeconds <= requirements.maxDurationSeconds
})

export const isMediaProbeCompliant = (probe: MediaProbe, requirements: MediaRequirements): boolean => {
  const checks = getMediaProbeCheckResults(probe, requirements)
  return checks.aspectRatioPass && checks.durationPass
}

// A workspace library serves several channels with different limits, so an asset passes triage
// when it fits at least one of them.
export const isMediaProbeCompliantForAny = (probe: MediaProbe, channelRequirements: MediaRequirements[]): boolean =>
  channelRequirements.some((requirements) => isMediaProbeCompliant(probe, requirements))

export const AssetAnalysisCategoryEnum = z.enum([
  'performance',
  'polished_music_video',
  'studio',
  'behind_the_scenes',
  'talking_head',
  'crowd',
  'lyric_visualizer',
  'other'
])
export type AssetAnalysisCategory = z.infer<typeof AssetAnalysisCategoryEnum>

export const NovaClassifierOutputSchema = z.object({ category: AssetAnalysisCategoryEnum }).strict()
export type NovaClassifierOutput = z.infer<typeof NovaClassifierOutputSchema>

// Where classification gave up: `projection` is Rally's own bounded-evidence guard failing
// before any provider call; `provider` is the LLM call itself. Only `provider` carries the
// LLM diagnostic fields.
export const AssetClassifierStageEnum = z.enum(['projection', 'provider'])
export type AssetClassifierStage = z.infer<typeof AssetClassifierStageEnum>

export const AssetClassifierDiagnosticSchema = z
  .object({
    stage: AssetClassifierStageEnum,
    ...LLMFailureDiagnosticSchema.shape
  })
  .strict()
export type AssetClassifierDiagnostic = z.infer<typeof AssetClassifierDiagnosticSchema>

export const TranscriptSegmentSchema = z
  .object({
    text: z.string().trim().min(1).max(2_000),
    startMs: z.number().int().nonnegative().nullable(),
    endMs: z.number().int().nonnegative().nullable()
  })
  .refine(({ startMs, endMs }) => startMs === null || endMs === null || endMs >= startMs, {
    path: ['endMs'],
    message: 'endMs must be greater than or equal to startMs'
  })
export type TranscriptSegment = z.infer<typeof TranscriptSegmentSchema>

export const OcrEntrySchema = z
  .object({
    text: z.string().trim().min(1).max(1_000),
    startMs: z.number().int().nonnegative().nullable(),
    endMs: z.number().int().nonnegative().nullable()
  })
  .refine(({ startMs, endMs }) => startMs === null || endMs === null || endMs >= startMs, {
    path: ['endMs'],
    message: 'endMs must be greater than or equal to startMs'
  })
export type OcrEntry = z.infer<typeof OcrEntrySchema>

export const AnalysisErrorSchema = z.object({
  code: z.string().trim().min(1).max(100),
  message: z.string().trim().min(1).max(500),
  retryable: z.boolean()
})
export type AnalysisError = z.infer<typeof AnalysisErrorSchema>

const LedgerCoordinatesSchema = z.object({
  workspaceId: idSchema,
  assetId: idSchema,
  generation: z.number().int().positive(),
  attempt: z.number().int().min(1).max(3)
})

export const AssetAnalysisIdentitySchema = z
  .object({
    workspaceId: idSchema,
    assetId: idSchema,
    namespace: z.uuid(),
    inputKey: z.string().startsWith('input/'),
    outputPrefix: z.string().startsWith('output/').endsWith('/'),
    // Version recorded when the identity was created. Historical only: the authoritative value for
    // staleness is the one on each result row, since normalization can change between generations.
    normalizationVersion: normalizationVersionSchema,
    createdAt: msTimestampSchema
  })
  .superRefine((row, ctx) => {
    if (!row.inputKey.startsWith(`input/${row.namespace}/`)) {
      ctx.addIssue({ code: 'custom', path: ['inputKey'], message: 'inputKey must be scoped to namespace' })
    }
    if (row.outputPrefix !== `output/${row.namespace}/`) {
      ctx.addIssue({ code: 'custom', path: ['outputPrefix'], message: 'outputPrefix must be scoped to namespace' })
    }
  })
export type AssetAnalysisIdentityRow = z.infer<typeof AssetAnalysisIdentitySchema>

export const AssetAnalysisRunSchema = z
  .object({
    ...LedgerCoordinatesSchema.omit({ attempt: true }).shape,
    origin: z.enum(['initial', 'manual']),
    createdAt: msTimestampSchema
  })
  .superRefine(({ generation, origin }, ctx) => {
    const expectedOrigin = generation === 1 ? 'initial' : 'manual'
    if (origin !== expectedOrigin) {
      ctx.addIssue({
        code: 'custom',
        path: ['origin'],
        message: `generation ${generation} requires origin ${expectedOrigin}`
      })
    }
  })
export type AssetAnalysisRunRow = z.infer<typeof AssetAnalysisRunSchema>

export const AssetAnalysisAttemptSchema = LedgerCoordinatesSchema.extend({
  clientToken: z.string().min(1),
  claimedAt: msTimestampSchema
})
export type AssetAnalysisAttemptRow = z.infer<typeof AssetAnalysisAttemptSchema>

export const AssetAnalysisInvocationSchema = LedgerCoordinatesSchema.extend({
  invocationArn: z.string().startsWith('arn:aws:bedrock:'),
  submittedAt: msTimestampSchema
})
export type AssetAnalysisInvocationRow = z.infer<typeof AssetAnalysisInvocationSchema>

const AssetAnalysisResultBaseSchema = LedgerCoordinatesSchema.extend({
  normalizationVersion: normalizationVersionSchema,
  bdaStatus: z.string().max(100).nullable().default(null),
  summary: z.string().max(4_000).nullable().default(null),
  transcript: z.array(TranscriptSegmentSchema).max(1_000).default([]),
  ocr: z.array(OcrEntrySchema).max(1_000).default([]),
  mediaProbe: MediaProbeSchema.nullable().default(null),
  completedAt: msTimestampSchema
})

export const AssetAnalysisResultSchema = z.discriminatedUnion('status', [
  AssetAnalysisResultBaseSchema.extend({
    status: z.literal('succeeded'),
    category: AssetAnalysisCategoryEnum,
    mediaProbe: MediaProbeSchema,
    error: z.null().default(null)
  }),
  AssetAnalysisResultBaseSchema.extend({
    status: z.literal('failed'),
    category: z.null().default(null),
    error: AnalysisErrorSchema
  })
])
export type AssetAnalysisResultRow = z.infer<typeof AssetAnalysisResultSchema>

export const AssetAnalysisStoredResultSchema = z.discriminatedUnion('status', [
  AssetAnalysisResultBaseSchema.extend({
    status: z.literal('succeeded'),
    category: AssetAnalysisCategoryEnum,
    mediaProbe: MediaProbeSchema.nullable().default(null),
    error: z.null().default(null)
  }),
  AssetAnalysisResultBaseSchema.extend({
    status: z.literal('failed'),
    category: z.null().default(null),
    error: AnalysisErrorSchema
  })
])
export type AssetAnalysisStoredResultRow = z.infer<typeof AssetAnalysisStoredResultSchema>

export const MAX_ANALYSIS_RESPONSE_SUMMARY_CHARS = 4_000
export const MAX_ANALYSIS_RESPONSE_TRANSCRIPT_SEGMENTS = 100
export const MAX_ANALYSIS_RESPONSE_OCR_ENTRIES = 100

const PublicTranscriptSegmentSchema = TranscriptSegmentSchema.strict()
const PublicOcrEntrySchema = OcrEntrySchema.strict()
const PublicAnalysisErrorSchema = AnalysisErrorSchema.strict()

const AssetAnalysisResponseBaseSchema = z
  .object({
    generation: z.number().int().positive(),
    attempt: z.number().int().nonnegative(),
    canRetry: z.boolean(),
    summary: z.string().max(MAX_ANALYSIS_RESPONSE_SUMMARY_CHARS).nullable(),
    transcript: z.array(PublicTranscriptSegmentSchema).max(MAX_ANALYSIS_RESPONSE_TRANSCRIPT_SEGMENTS),
    ocr: z.array(PublicOcrEntrySchema).max(MAX_ANALYSIS_RESPONSE_OCR_ENTRIES),
    mediaRequirements: MediaRequirementsSchema,
    mediaProbe: MediaProbeSchema.nullable()
  })
  .strict()

export const AssetAnalysisResponseSchema = z.discriminatedUnion('status', [
  AssetAnalysisResponseBaseSchema.extend({
    status: z.literal(AnalysisStatusEnum.enum.pending),
    category: z.null(),
    mediaProbe: z.null(),
    error: z.null()
  }),
  AssetAnalysisResponseBaseSchema.extend({
    status: z.literal(AnalysisStatusEnum.enum.analyzing),
    category: z.null(),
    mediaProbe: z.null(),
    error: z.null()
  }),
  AssetAnalysisResponseBaseSchema.extend({
    status: z.literal(AnalysisStatusEnum.enum.succeeded),
    category: AssetAnalysisCategoryEnum,
    mediaProbe: MediaProbeSchema,
    error: z.null()
  }),
  AssetAnalysisResponseBaseSchema.extend({
    status: z.literal(AnalysisStatusEnum.enum.failed),
    category: z.null(),
    mediaProbe: z.null(),
    error: PublicAnalysisErrorSchema
  })
])
export type AssetAnalysisResponse = z.infer<typeof AssetAnalysisResponseSchema>

export const AssetAnalysisRetryErrorCodeEnum = z.enum(['ANALYSIS_INPUT_REUPLOAD_REQUIRED', 'ANALYSIS_INPUT_TOO_LARGE'])
export type AssetAnalysisRetryErrorCode = z.infer<typeof AssetAnalysisRetryErrorCodeEnum>

export const AssetAnalysisRetryErrorResponseSchema = z.object({
  error: z.object({
    code: AssetAnalysisRetryErrorCodeEnum,
    message: z.string().trim().min(1).max(200)
  })
})
export type AssetAnalysisRetryErrorResponse = z.infer<typeof AssetAnalysisRetryErrorResponseSchema>

export const AssetAnalysisRetryResponseSchema = z.object({
  analysisStatus: AnalysisStatusEnum,
  generation: z.number().int().positive()
})
export type AssetAnalysisRetryResponse = z.infer<typeof AssetAnalysisRetryResponseSchema>

export const AssetAnalysisTombstoneSchema = z.object({
  workspaceId: idSchema,
  assetId: idSchema,
  deletedAt: msTimestampSchema
})
export type AssetAnalysisTombstoneRow = z.infer<typeof AssetAnalysisTombstoneSchema>

// These aliases keep the record names convenient for callers while each schema remains immutable and record-specific.
export const IdentitySchema = AssetAnalysisIdentitySchema
export const RunSchema = AssetAnalysisRunSchema
export const AttemptSchema = AssetAnalysisAttemptSchema
export const InvocationSchema = AssetAnalysisInvocationSchema
export const ResultSchema = AssetAnalysisResultSchema
export const TombstoneSchema = AssetAnalysisTombstoneSchema
